import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, FileText, ChevronRight } from 'lucide-react';
import Shell from '../../components/TopBarShell';
import StatusBadge from '../../components/StatusBadge';
import OdooBadge from '../../components/OdooBadge';
import { Button } from '../../components/ui/button';
import { useReviewQueue } from '../../contexts/ReviewQueueContext';
import { useLocale, useT } from '../../contexts/LocaleProvider';
import api from '../../utils/api';

const fmtMoney = (n) => `€ ${Number(n || 0).toFixed(2)}`;

const ExpensesList = () => {
  const { count: reviewCount } = useReviewQueue();
  const t = useT();
  const { intlLocale } = useLocale();
  const fmtDate = (iso) => iso ? new Intl.DateTimeFormat(intlLocale).format(new Date(iso)) : '·';
  const [reports, setReports] = useState(null);

  useEffect(() => {
    api.expenses().then(setReports).catch(() => setReports([]));
  }, []);

  return (
    <Shell reviewCount={reviewCount}>
      <header className="mb-6 flex items-end justify-between">
        <div>
          <h1 className="font-display text-[1.75rem]">{t('expense.title')}</h1>
          <p className="text-[15px] text-[hsl(var(--text-soft))] mt-1.5">
            {t('expense.intro')}
          </p>
        </div>
        <Link to="/expenses/new">
          <Button className="gap-2"><Plus className="h-4 w-4" /> {t('expense.new')}</Button>
        </Link>
      </header>

      <div className="bg-[hsl(var(--surface))] border border-border rounded-lg overflow-hidden">
        {reports === null && (
          <div className="px-5 py-12 text-center text-sm text-[hsl(var(--text-soft))]">{t('common.loading')}</div>
        )}
        {reports !== null && reports.length === 0 && (
          <div className="px-5 py-14 text-center">
            <FileText className="h-7 w-7 mx-auto text-[hsl(var(--text-muted))]" />
            <div className="mt-3 text-sm font-medium">{t('expense.empty')}</div>
            <p className="text-xs text-[hsl(var(--text-soft))] mt-1">
              {t('expense.empty.hint')}
            </p>
          </div>
        )}
        {reports !== null && reports.length > 0 && (
          <>
            <div className="px-5 py-3 grid grid-cols-12 gap-4 text-[13px] text-[hsl(var(--text-muted))] border-b border-border bg-[hsl(var(--bg-subtle))]">
              <div className="col-span-5">{t('expense.col.note')}</div>
              <div className="col-span-2">{t('expense.col.date')}</div>
              <div className="col-span-2 text-right">{t('expense.col.tvac')}</div>
              <div className="col-span-3 text-right">{t('expense.col.status')}</div>
            </div>
            <ul className="divide-y divide-[hsl(var(--rule))]">
              {reports.map(r => (
                <li key={r.id}>
                  <Link to={`/expenses/${r.id}`} className="px-5 py-3 grid grid-cols-12 gap-4 items-center hover:bg-[hsl(var(--surface-2))]">
                    <div className="col-span-5 min-w-0">
                      <div className="text-[15px] truncate">{r.title || t('expense.noteOf', { date: fmtDate(r.created_at) })}</div>
                      <div className="text-[13px] text-[hsl(var(--text-muted))] font-num">
                        NDF-{String(r.id).padStart(5, '0')} · {t(r.lines.length > 1 ? 'expense.lines.short.many' : 'expense.lines.short', { n: r.lines.length })}
                      </div>
                    </div>
                    <div className="col-span-2 text-[14px] text-[hsl(var(--text-soft))]">
                      {fmtDate(r.submitted_at || r.created_at)}
                    </div>
                    <div className="col-span-2 text-[15px] text-right font-num">{fmtMoney(r.total_tvac)}</div>
                    <div className="col-span-3 flex justify-end items-center gap-2">
                      {r.odoo?.state === 'paid' && <OdooBadge odoo={r.odoo} link={false} />}
                      <StatusBadge status={r.status} />
                      <ChevronRight className="h-4 w-4 text-[hsl(var(--text-muted))]" />
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </Shell>
  );
};

export default ExpensesList;
